import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import { formatLocalTime } from "@/utils/formatLocalTime"; // 🆕 [P1 ext] timezone Québec centralisé
import {
  CheckSquare, Plus, Trash2, Check, Clock, User, Calendar,
  Filter, AlertTriangle, Edit2, X
} from "lucide-react";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const STATUT_CONFIG = {
  en_cours: { label: "En cours", color: "bg-blue-100 text-blue-800 border-blue-200" },
  complete: { label: "Complété", color: "bg-green-100 text-green-800 border-green-200" },
};

function isPast(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr + "T23:59:59") < new Date();
}

function isDueSoon(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr + "T23:59:59");
  const diff = (d - new Date()) / 86400000;
  return diff >= 0 && diff <= 3;
}

function formatDate(dateStr) {
  if (!dateStr) return "Sans échéance";
  // 🆕 [P1 ext] formatLocalTime avec weekday short
  return formatLocalTime(dateStr + "T00:00:00", { dateOnly: true, weekday: 'short' });
}

function getDaysUntilArchive(completedAt) {
  if (!completedAt) return null;
  const done = new Date(completedAt);
  const expire = new Date(done.getTime() + 3 * 86400000);
  const remaining = Math.ceil((expire - new Date()) / 86400000);
  return remaining;
}

export default function TodoList() {
  const { user, token } = useAuth();
  const { canDo } = usePermissions() || {};
  const [todos, setTodos] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useState("en_cours");
  const [showForm, setShowForm] = useState(false);
  const [editTodo, setEditTodo] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    titre: "",
    description: "",
    assigned_to: [],
    due_date: "",
  });

  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via `todo_creer`.
  // Contrôle : bouton "Nouvelle tâche", bouton "Modifier", suppression.
  // Fail-secure : canDo=false quand permissions non chargées.
  const canCreateTodo = !!(canDo && canDo('todo_creer'));

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const loadTodos = async () => {
    setLoading(true);
    try {
      const params = filtre !== "tous" ? `?statut=${filtre}` : "";
      const res = await axios.get(`${API_URL}/api/todos${params}`, { headers });
      if (res.status === 200) {
        const data = res.data;
        setTodos(data.todos || []);
      }
    } catch (e) {
      toast.error("Erreur chargement tâches");
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees`, { headers });
      if (res.status === 200) {
        const data = res.data;
        setEmployees(data.employees || data || []);
      }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadTodos(); }, [filtre]);
  useEffect(() => { loadEmployees(); }, []);

  const openCreate = () => {
    setEditTodo(null);
    setForm({ titre: "", description: "", assigned_to: [], due_date: "" });
    setShowForm(true);
  };

  const openEdit = (todo) => {
    setEditTodo(todo);
    setForm({
      titre: todo.titre,
      description: todo.description || "",
      assigned_to: todo.assigned_to || [],
      due_date: todo.due_date || "",
    });
    setShowForm(true);
  };

  const toggleAssign = (empId) => {
    setForm(f => ({
      ...f,
      assigned_to: f.assigned_to.includes(empId)
        ? f.assigned_to.filter(id => id !== empId)
        : [...f.assigned_to, empId],
    }));
  };

  const getAssignedNoms = () =>
    employees
      .filter(e => form.assigned_to.includes(e.id))
      .map(e => `${e.prenom} ${e.nom}`);

  const handleSubmit = async () => {
    if (!form.titre.trim()) { toast.error("Le titre est obligatoire"); return; }
    setSaving(true);
    try {
      const payload = {
        titre: form.titre,
        description: form.description,
        assigned_to: form.assigned_to,
        assigned_noms: getAssignedNoms(),
        due_date: form.due_date || null,
      };

      let res;
      if (editTodo) {
        res = await axios.put(`${API_URL}/api/todos/${editTodo.id}`, payload, { headers });
      } else {
        res = await axios.post(`${API_URL}/api/todos`, payload, { headers });
      }

      if (res.status !== 200) throw new Error();
      toast.success(editTodo ? "Tâche mise à jour" : "Tâche créée");
      setShowForm(false);
      loadTodos();
    } catch (e) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (todo) => {
    try {
      await axios.put(`${API_URL}/api/todos/${todo.id}`, { statut: todo.statut === "complete" ? "en_cours" : "complete" }, { headers });
      toast.success(todo.statut === "complete" ? "Tâche réouverte" : "Tâche marquée complète ✓");
      loadTodos();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette tâche ?")) return;
    try {
      await axios.delete(`${API_URL}/api/todos/${id}`, { headers });
      toast.success("Tâche supprimée");
      loadTodos();
    } catch (e) { toast.error("Erreur suppression"); }
  };

  const [mesTachesOnly, setMesTachesOnly] = useState(false);

  const todosAffichés = mesTachesOnly
    ? todos.filter(t => (t.assigned_to || []).includes(user?.employee_id || user?.id))
    : todos;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CheckSquare className="w-6 h-6 text-blue-600" /> To Do List
          </h1>
          <p className="text-slate-500 text-sm mt-1">Tâches et suivis de l'équipe</p>
        </div>
        {canCreateTodo && (
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="btn-nouvelle-tache">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle tâche
          </Button>
        )}
      </div>

      {/* Filtres */}
      <div className="flex gap-2 mb-6 flex-wrap items-center">
        {[
          { key: "en_cours", label: "En cours" },
          { key: "complete", label: "Complétées" },
          { key: "tous", label: "Toutes" },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFiltre(f.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${
              filtre === f.key
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
            }`}
            data-testid={`filtre-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto">
          <button
            onClick={() => setMesTachesOnly(!mesTachesOnly)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${
              mesTachesOnly
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"
            }`}
            data-testid="filtre-mes-taches"
          >
            Mes tâches seulement
          </button>
        </div>
      </div>

      {/* Statistiques */}
      {!loading && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: "En cours", count: todos.filter(t => t.statut === "en_cours").length, color: "text-blue-600" },
            { label: "Complétées", count: todos.filter(t => t.statut === "complete").length, color: "text-green-600" },
            { label: "En retard", count: todos.filter(t => t.statut === "en_cours" && isPast(t.due_date)).length, color: "text-red-600" },
          ].map(s => (
            <Card key={s.label} className="text-center py-2">
              <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </Card>
          ))}
        </div>
      )}

      {/* Liste des tâches */}
      {loading ? (
        <p className="text-center text-slate-400 py-8">Chargement...</p>
      ) : todosAffichés.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Aucune tâche pour ce filtre</p>
          {canCreateTodo && (
            <Button variant="outline" className="mt-4" onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" /> Créer une tâche
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {todosAffichés.map(todo => {
            const past = isPast(todo.due_date) && todo.statut === "en_cours";
            const soon = isDueSoon(todo.due_date) && !past && todo.statut === "en_cours";
            const done = todo.statut === "complete";
            const daysLeft = done ? getDaysUntilArchive(todo.completed_at) : null;
            return (
              <Card
                key={todo.id}
                className={`transition-all ${done ? "bg-green-50 border-green-200" : past ? "border-red-200 bg-red-50" : soon ? "border-yellow-200 bg-yellow-50" : "border-slate-200"}`}
                data-testid={`todo-card-${todo.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Check button */}
                    <button
                      onClick={() => handleComplete(todo)}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                        done ? "bg-green-500 border-green-500 text-white" : "border-slate-300 hover:border-green-400"
                      }`}
                      data-testid={`btn-complete-${todo.id}`}
                    >
                      {done && <Check className="w-3.5 h-3.5" />}
                    </button>

                    <div className="flex-1 min-w-0">
                      {/* Titre + badges */}
                      <div className="flex items-start justify-between gap-2">
                        <h3 className={`font-semibold ${done ? "text-green-800" : "text-slate-800"}`}>
                          {todo.titre}
                        </h3>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {past && <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">En retard</Badge>}
                          {soon && <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 text-[10px]">Bientôt dû</Badge>}
                          {done && (
                            <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]">
                              ✓ Fait
                              {daysLeft !== null && daysLeft > 0 && ` · archivé dans ${daysLeft}j`}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      {todo.description && (
                        <p className={`text-sm mt-1 ${done ? "text-green-700" : "text-slate-600"}`}>{todo.description}</p>
                      )}

                      {/* Méta */}
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                        {todo.due_date && (
                          <span className={`flex items-center gap-1 ${past ? "text-red-600 font-semibold" : soon ? "text-yellow-700 font-semibold" : ""}`}>
                            <Calendar className="w-3 h-3" /> {formatDate(todo.due_date)}
                          </span>
                        )}
                        {todo.assigned_noms?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" /> {todo.assigned_noms.join(", ")}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-slate-400">
                          <Clock className="w-3 h-3" /> par {todo.created_by_nom}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    {canCreateTodo && (
                      <div className="flex gap-1">
                        {!done && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(todo)}
                            className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
                            data-testid={`btn-edit-${todo.id}`}
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(todo.id)}
                          className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                          data-testid={`btn-delete-${todo.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog création/édition */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editTodo ? "Modifier la tâche" : "Nouvelle tâche"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Titre *</label>
              <Input
                className="mt-1"
                placeholder="Ex: Commander la peinture pour le projet X"
                value={form.titre}
                onChange={e => setForm(f => ({ ...f, titre: e.target.value }))}
                data-testid="input-todo-titre"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Description (optionnel)</label>
              <Textarea
                className="mt-1"
                rows={3}
                placeholder="Détails supplémentaires..."
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                data-testid="textarea-todo-description"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Date d'échéance</label>
              <Input
                type="date"
                className="mt-1"
                value={form.due_date}
                onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                data-testid="input-todo-due-date"
              />
              {form.due_date && (
                <p className="text-xs text-slate-500 mt-1">
                  Un SMS de rappel sera envoyé 3 jours avant l'échéance.
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Assigner à</label>
              <div className="mt-2 flex flex-wrap gap-2 max-h-40 overflow-y-auto p-1">
                {employees.map(emp => (
                  <button
                    key={emp.id}
                    onClick={() => toggleAssign(emp.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      form.assigned_to.includes(emp.id)
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                    }`}
                  >
                    {emp.prenom} {emp.nom}
                  </button>
                ))}
              </div>
              {form.assigned_to.length > 0 && (
                <p className="text-xs text-blue-600 mt-1">
                  {form.assigned_to.length} personne(s) assignée(s)
                </p>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="btn-submit-todo"
              >
                {saving ? "Sauvegarde..." : editTodo ? "Mettre à jour" : "Créer la tâche"}
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>
                Annuler
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
